/**
 * studentService.ts
 * Firestore operations for class rosters and student XP/star management.
 *
 * Schema:
 *   /classes/{classId}          — ClassRoom document
 *   /classes/{classId}/students/{studentId}  — Student sub-collection
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  query,
  orderBy,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirestoreDb, getCurrentUid, ensureDataSession } from './firebase';
import type { Student, ClassRoom, AvatarEmoji } from '@types-local/class.types';
import {
  getLevelForXP,
} from '@types-local/class.types';

// ---- XP config ---------------------------------------------

/** Stars-to-XP conversion rate */
const XP_PER_STAR = 10;
/** XP awarded per correct MC answer */
const XP_PER_CORRECT = 20;
/** XP bonus for fastest answer in competition mode */
const XP_SPEED_BONUS = 15;

// ---- Path helpers ------------------------------------------

const db = () => getFirestoreDb();
const classDocRef = (classId: string) => doc(db(), 'classes', classId);
const studentsColRef = (classId: string) =>
  collection(db(), 'classes', classId, 'students');
const studentDocRef = (classId: string, studentId: string) =>
  doc(db(), 'classes', classId, 'students', studentId);

// ---- Class CRUD --------------------------------------------

/**
 * Lists all classes owned by a teacher, most recent first. Used after
 * login to let the teacher pick a saved class instead of re-creating one.
 */
export async function getClassesByTeacher(teacherId: string): Promise<ClassRoom[]> {
  // The Firestore query is only authorized once the DATA-project session is
  // established (it can be dropped after a task-pane reload), so restore it
  // before reading — mirrors createClass/addStudent.
  await ensureDataSession();
  const snap = await getDocs(query(collection(db(), 'classes'), where('teacherId', '==', teacherId)));
  return snap.docs
    .map((d) => d.data() as ClassRoom)
    .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
}

export async function createClass(name: string, coverColor: string): Promise<string> {
  await ensureDataSession();
  const uid = getCurrentUid();
  if (!uid) throw new Error('Not authenticated');

  const classId = crypto.randomUUID();
  const classRoom: ClassRoom = {
    id: classId,
    name,
    teacherId: uid,
    createdAt: Date.now(),
    studentCount: 0,
    coverColor,
  };

  await setDoc(classDocRef(classId), {
    ...classRoom,
    createdAt: serverTimestamp(),
  });
  return classId;
}

export async function getClass(classId: string): Promise<ClassRoom | null> {
  const snap = await getDoc(classDocRef(classId));
  return snap.exists() ? (snap.data() as ClassRoom) : null;
}

// ---- Student CRUD ------------------------------------------

export async function addStudent(
  classId: string,
  name: string,
  avatar: AvatarEmoji,
): Promise<string> {
  await ensureDataSession();
  const studentId = crypto.randomUUID();
  const student: Student = {
    id: studentId,
    name,
    avatar,
    xp: 0,
    level: 1,
    totalStars: 0,
    joinedAt: Date.now(),
  };
  await setDoc(studentDocRef(classId, studentId), student);
  // Increment class student count
  await updateDoc(classDocRef(classId), {
    studentCount: (await getDoc(classDocRef(classId))).data()?.studentCount + 1 || 1,
  });
  return studentId;
}

export async function removeStudent(
  classId: string,
  studentId: string,
): Promise<void> {
  await deleteDoc(studentDocRef(classId, studentId));
}

// ---- XP & Star transactions --------------------------------

/**
 * Awards stars to a student. Atomically increments totalStars and XP,
 * then checks for level-up.
 * Returns { newLevel, leveledUp } so the caller can trigger animations.
 */
export async function awardStars(
  classId: string,
  studentId: string,
  stars: number,
): Promise<{ newLevel: number; leveledUp: boolean }> {
  const ref = studentDocRef(classId, studentId);

  return runTransaction(db(), async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Student not found');

    const student = snap.data() as Student;
    const xpGain = stars * XP_PER_STAR;
    const newXP = student.xp + xpGain;
    const oldLevel = student.level;
    const newLevel = getLevelForXP(newXP);
    const leveledUp = newLevel > oldLevel;

    tx.update(ref, {
      totalStars: student.totalStars + stars,
      xp: newXP,
      level: newLevel,
    });

    return { newLevel, leveledUp };
  });
}

/**
 * Awards XP for a correct quiz answer, with optional speed bonus.
 */
export async function awardQuizXP(
  classId: string,
  studentId: string,
  isCorrect: boolean,
  isSpeedBonus: boolean = false,
): Promise<{ newLevel: number; leveledUp: boolean }> {
  if (!isCorrect) return { newLevel: 0, leveledUp: false };

  const ref = studentDocRef(classId, studentId);
  return runTransaction(db(), async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Student not found');

    const student = snap.data() as Student;
    const xpGain = XP_PER_CORRECT + (isSpeedBonus ? XP_SPEED_BONUS : 0);
    const newXP = student.xp + xpGain;
    const oldLevel = student.level;
    const newLevel = getLevelForXP(newXP);
    const leveledUp = newLevel > oldLevel;

    tx.update(ref, { xp: newXP, level: newLevel });
    return { newLevel, leveledUp };
  });
}

/**
 * Resolves a roster student's document ID by their display name
 * (case-insensitive). Students join by typing a name, which isn't the
 * roster document ID, so scoring matches them back to the roster here.
 * Returns null if no roster student matches.
 */
export async function findStudentIdByName(
  classId: string,
  name: string,
): Promise<string | null> {
  const target = name.trim().toLowerCase();
  if (!target) return null;
  const snap = await getDocs(studentsColRef(classId));
  const match = snap.docs.find(
    (d) => ((d.data() as Student).name ?? '').trim().toLowerCase() === target,
  );
  return match ? match.id : null;
}

/**
 * Fetches the full student roster once, ordered by XP descending.
 * Used where the in-memory store may not be populated (e.g. building
 * the on-slide leaderboard right after scoring).
 */
export async function getRoster(classId: string): Promise<Student[]> {
  const snap = await getDocs(studentsColRef(classId));
  return snap.docs
    .map((d) => d.data() as Student)
    .sort((a, b) => b.xp - a.xp);
}

// ---- Real-time roster subscription -------------------------

/**
 * Subscribes to the full student roster for a class, ordered by XP descending.
 * Returns an unsubscribe function.
 */
export function subscribeToRoster(
  classId: string,
  callback: (students: Student[]) => void,
): Unsubscribe {
  const q = query(studentsColRef(classId), orderBy('xp', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const students = snapshot.docs.map((d) => d.data() as Student);
    callback(students);
  });
}
